import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Clock, Download, Eye, Loader2, TrendingUp, AlertTriangle, Activity, Zap, RefreshCw } from "lucide-react";
import AppShell from "@/components/AppShell";
import { RangeFilter } from "@/components/RangeFilter";
import { UpgradeCard } from "@/components/UpgradeCard";
import { rangeQuery, labelFor } from "@/lib/dateRange";
import StatusBadge from "@/components/StatusBadge";
import { api, API, apiError } from "@/lib/api";
import { compactMoney, formatMoney, relative } from "@/lib/format";
import { Button } from "@/components/ui/button";


const KpiCard = ({ label, value, hint, testid, accent }) => (
  <div data-testid={testid} className="nv-card p-4 sm:p-5 nv-rise">
    <p className="text-[10px] font-num uppercase tracking-[0.18em] text-zinc-500">{label}</p>
    <p className={`mt-2 text-2xl sm:text-3xl font-bold font-num tracking-tight ${accent || "text-zinc-100"}`}>{value}</p>
    {hint && <p className="mt-1 text-xs text-zinc-500">{hint}</p>}
  </div>
);

const chartAxis = { stroke: "#52525B", fontSize: 11 };
const tooltipStyle = {
  background: "#18181B",
  border: "1px solid #27272A",
  borderRadius: 10,
  fontSize: 12,
  color: "#FAFAFA",
};

const STATUS_COLORS = {
  draft: "#71717A",
  sent: "#3B82F6",
  viewed: "#F59E0B",
  accepted: "#10B981",
  declined: "#EF4444",
  expired: "#52525B",
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [range, setRange] = useState({ preset: "30d", custom: {} });
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [live, setLive] = useState(true);
  const [tick, setTick] = useState(0);
  const [credits, setCredits] = useState(null);
  const [curr, setCurr] = useState("");

  const fetchData = useCallback(
    (silent) => {
      if (!silent) setData(null);
      api
        .get(`/analytics/overview?${rangeQuery(range.preset, range.custom)}${curr ? `&currency=${curr}` : ""}`)
        .then((r) => setData(r.data))
        .catch((e) => setError(apiError(e)));
      api.get("/billing/subscription").then((r) => setCredits(r.data)).catch(() => {});
    },
    [range, curr]
  );

  useEffect(() => {
    fetchData(false);
  }, [fetchData]);

  useEffect(() => {
    if (!live) return;
    const id = setInterval(() => {
      fetchData(true);
      setTick((t) => t + 1);
    }, 20000);
    return () => clearInterval(id);
  }, [live, fetchData]);

  const k = data?.kpis;
  const currency = data?.currency || "INR";

  return (
    <AppShell
      title="Analytics"
      subtitle={`Everything happening across your proposals · ${labelFor(range.preset, range.custom)}`}
      actions={
        <div className="flex items-center gap-2">
          <button
            data-testid="live-toggle-btn"
            onClick={() => setLive((l) => !l)}
            className={`hidden sm:inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border text-xs font-num transition-colors ${
              live ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-zinc-800 text-zinc-500"
            }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${live ? "bg-emerald-400 animate-pulse" : "bg-zinc-600"}`} />
            {live ? "Live" : "Paused"}
          </button>
          <button
            data-testid="refresh-btn"
            onClick={() => fetchData(true)}
            className="h-9 w-9 grid place-items-center rounded-lg border border-zinc-800 text-zinc-400 hover:text-zinc-100 transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          <RangeFilter
            testid="dashboard-range"
            preset={range.preset}
            custom={range.custom}
            onChange={setRange}
          />
          <Button
            data-testid="export-csv-btn"
            variant="outline"
            className="h-9 border-zinc-800 text-zinc-300 hover:bg-zinc-800"
            onClick={() => window.open(`${API}/analytics/export.csv?days=365`, "_blank")}
          >
            <Download className="w-4 h-4 sm:mr-1.5" /> <span className="hidden sm:inline">CSV</span>
          </Button>
        </div>
      }
    >
      {error && <p className="text-sm text-red-400 mb-4">{error}</p>}
      {!data ? (
        <div className="h-64 grid place-items-center text-zinc-600">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : (
        <div className="space-y-6">
          <UpgradeCard sub={credits} />
          <div data-testid="hero-revenue-panel" className="relative overflow-hidden rounded-2xl border border-zinc-800/80 bg-gradient-to-br from-[#141428] via-panel to-panel p-6 sm:p-7">
            <div className="absolute -top-24 -right-16 w-72 h-72 rounded-full bg-indigo-500/12 blur-3xl" />
            <div className="relative grid lg:grid-cols-3 gap-6 items-end">
              <div className="lg:col-span-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-[10px] font-num uppercase tracking-[0.2em] text-zinc-500">Revenue closed</p>
                  <span className="inline-flex items-center gap-1 text-[10px] font-num text-emerald-400">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> live
                  </span>
                  {(data.currencies?.length || 0) > 1 && (
                    <div data-testid="currency-switcher" className="flex items-center gap-1 ml-auto rounded-lg border border-zinc-800 bg-black/30 p-0.5">
                      {data.currencies.map((c) => (
                        <button
                          key={c.code}
                          data-testid={`currency-option-${c.code}`}
                          onClick={() => setCurr(c.code)}
                          title={`${c.sent} proposals · ${c.revenue_closed_formatted} won`}
                          className={`px-2 py-0.5 rounded-md text-[11px] font-num transition-colors ${
                            currency === c.code ? "bg-indigo-500 text-white" : "text-zinc-400 hover:text-zinc-100"
                          }`}
                        >
                          {c.code}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <p data-testid="hero-revenue-value" className="mt-2 text-4xl sm:text-5xl font-extrabold font-num tracking-tight">
                  {compactMoney(k.revenue_closed_minor, currency)}
                </p>
                <p className="mt-1 text-sm text-zinc-400 font-num">
                  {formatMoney(k.revenue_closed_minor, currency)} · {k.accepted} deals won
                </p>
                {data.other_currency_count > 0 && (
                  <p data-testid="other-currency-note" className="mt-1.5 text-xs text-amber-400/90">
                    {data.other_currency_count} more proposal{data.other_currency_count === 1 ? "" : "s"} billed in{" "}
                    {data.currencies.filter((c) => c.code !== currency).map((c) => c.code).join(", ")} — switch above to see
                    them. Currencies are never added together.
                  </p>
                )}
                <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2">
                  {[
                    ["Pipeline", compactMoney(k.total_value_minor, currency)],
                    ["Win rate", `${k.acceptance_rate}%`],
                    ["Avg deal", compactMoney(k.avg_deal_minor, currency)],
                  ].map(([l, v]) => (
                    <div key={l}>
                      <p className="text-[10px] font-num uppercase tracking-[0.16em] text-zinc-500">{l}</p>
                      <p className="text-base font-semibold font-num text-zinc-100">{v}</p>
                    </div>
                  ))}
                </div>
                {credits && (
                  <button
                    data-testid="dashboard-credits-widget"
                    onClick={() => navigate("/plans")}
                    className="mt-5 w-full rounded-xl border border-zinc-800 bg-zinc-900/50 p-3 text-left hover:border-indigo-500/40 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1.5 text-xs text-zinc-400">
                        <Zap className="w-3.5 h-3.5 text-indigo-400" /> Credits left
                      </span>
                      <span data-testid="dashboard-credits-left" className="text-lg font-bold font-num">
                        {credits.proposals_left}
                      </span>
                    </div>
                    <div className="mt-2 h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                      <div
                        className="h-full bg-indigo-500 transition-all duration-700"
                        style={{ width: `${Math.min(100, (credits.proposals_used / Math.max(1, credits.proposal_limit)) * 100)}%` }}
                      />
                    </div>
                    <p className="mt-1.5 text-[10px] font-num text-zinc-500">
                      1 credit = 1 proposal · buy more at ₹{credits.credit_price_inr || 15} each
                    </p>
                  </button>
                )}
              </div>

              <div className="lg:col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] font-num uppercase tracking-[0.2em] text-zinc-500">Last 14 days</p>
                  <p className="text-[10px] font-num text-zinc-600">updated {new Date(data.generated_at).toLocaleTimeString()}</p>
                </div>
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={data.daily}>
                    <defs>
                      <linearGradient id="gRev" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.55} />
                        <stop offset="100%" stopColor="#8B5CF6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#27272A" vertical={false} />
                    <XAxis dataKey="date" {...chartAxis} interval={2} />
                    <YAxis {...chartAxis} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Area type="monotone" dataKey="revenue" stroke="#8B5CF6" strokeWidth={2} fill="url(#gRev)" name="Revenue" />
                    <Area type="monotone" dataKey="created" stroke="#3F3F46" strokeWidth={1.5} fill="transparent" name="Created" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4">
            <KpiCard testid="kpi-sent" label="Proposals sent" value={k.sent} hint={`${k.accepted} accepted · ${k.declined} declined`} />
            <KpiCard testid="kpi-total-value" label="Pipeline value" value={compactMoney(k.total_value_minor, currency)} hint="All sent proposals" />
            <KpiCard testid="kpi-acceptance-rate" label="Acceptance rate" value={`${k.acceptance_rate}%`} accent="text-emerald-400" hint="Sent → accepted" />
            <KpiCard testid="kpi-revenue-closed" label="Revenue closed" value={compactMoney(k.revenue_closed_minor, currency)} accent="text-emerald-400" />
            <KpiCard testid="kpi-avg-deal" label="Avg deal size" value={compactMoney(k.avg_deal_minor, currency)} />
            <KpiCard testid="kpi-median-decision" label="Median decision" value={`${k.median_days_to_decision}d`} hint="Sent → decided" />
          </div>

          <div data-testid="activity-feed" className="nv-card p-5">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-indigo-400" />
              <h3 className="text-base font-semibold">Live activity</h3>
              <span className="ml-auto flex items-center gap-1.5 text-[10px] font-num text-zinc-500">
                auto-refresh 20s <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              </span>
            </div>
            <p className="text-xs text-zinc-500 mb-3">Newest movement across your pipeline</p>
            {!data.activity?.length ? (
              <p className="text-xs text-zinc-600 py-4">No activity yet.</p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6">
                {data.activity.map((a) => (
                  <button
                    key={a.id + (a.at || "")}
                    data-testid={`activity-row-${a.id}`}
                    onClick={() => navigate(`/editor/${a.id}`)}
                    className="text-left py-2.5 flex items-center gap-3 border-b border-zinc-800/60 hover:bg-zinc-800/30 px-2 -mx-2 rounded transition-colors"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm text-zinc-200 truncate">{a.title}</span>
                      <span className="block text-[11px] text-zinc-500 truncate">
                        {a.client_name} · {relative(a.at)}
                        {a.opens > 0 ? ` · ${a.opens} opens` : ""}
                      </span>
                    </span>
                    <span className="text-right shrink-0 space-y-1">
                      <span className="block text-xs font-num text-zinc-300">{a.amount_formatted}</span>
                      <StatusBadge status={a.status} testid={`activity-status-${a.id}`} />
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>


          <div className="grid lg:grid-cols-3 gap-4">
            <div data-testid="conversion-funnel" className="nv-card p-5 lg:col-span-1">
              <h3 className="text-base font-semibold">Conversion funnel</h3>
              <p className="text-xs text-zinc-500 mb-4">Where deals fall out</p>
              <div className="space-y-3">
                {data.funnel.map((f, i) => {
                  const max = data.funnel[0].count || 1;
                  const width = Math.max(6, (f.count / max) * 100);
                  return (
                    <div key={f.stage} data-testid={`funnel-stage-${f.stage.toLowerCase()}`}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-zinc-300">{f.stage}</span>
                        <span className="font-num text-zinc-400">
                          {f.count}
                          {i > 0 && <span className="text-zinc-600"> · {f.conversion}%</span>}
                        </span>
                      </div>
                      <div className="h-7 rounded-md bg-zinc-800/50 overflow-hidden">
                        <div
                          className="h-full rounded-md transition-all duration-700"
                          style={{
                            width: `${width}%`,
                            background: `linear-gradient(90deg,#6366F1,${["#6366F1", "#3B82F6", "#F59E0B", "#10B981"][i] || "#6366F1"})`,
                          }}
                        />
                      </div>
                      {i > 0 && f.drop_off > 0 && (
                        <p className="mt-1 text-[11px] text-amber-400/80 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> {f.drop_off} dropped off here
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div data-testid="trend-chart" className="nv-card p-5 lg:col-span-2">
              <h3 className="text-base font-semibold">Sent vs accepted</h3>
              <p className="text-xs text-zinc-500 mb-4">Monthly volume</p>
              <ResponsiveContainer width="100%" height={230}>
                <AreaChart data={data.trend}>
                  <defs>
                    <linearGradient id="gSent" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#6366F1" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#6366F1" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gAcc" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10B981" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#27272A" vertical={false} />
                  <XAxis dataKey="month" {...chartAxis} />
                  <YAxis {...chartAxis} allowDecimals={false} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Area type="monotone" dataKey="sent" stroke="#6366F1" fill="url(#gSent)" strokeWidth={2} />
                  <Area type="monotone" dataKey="accepted" stroke="#10B981" fill="url(#gAcc)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-4">
            <div data-testid="revenue-chart" className="nv-card p-5">
              <h3 className="text-base font-semibold">Revenue by month</h3>
              <p className="text-xs text-zinc-500 mb-4">Closed-won only</p>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={data.trend}>
                  <CartesianGrid stroke="#27272A" vertical={false} />
                  <XAxis dataKey="month" {...chartAxis} />
                  <YAxis {...chartAxis} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="revenue" fill="#6366F1" radius={[5, 5, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div data-testid="pipeline-status-chart" className="nv-card p-5">
              <h3 className="text-base font-semibold">Pipeline by status</h3>
              <p className="text-xs text-zinc-500 mb-4">Current distribution</p>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={data.by_status} layout="vertical">
                  <CartesianGrid stroke="#27272A" horizontal={false} />
                  <XAxis type="number" {...chartAxis} allowDecimals={false} />
                  <YAxis type="category" dataKey="status" {...chartAxis} width={62} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="count" radius={[0, 5, 5, 0]}>
                    {data.by_status.map((s) => (
                      <Cell key={s.status} fill={STATUS_COLORS[s.status]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div data-testid="win-rate-chart" className="nv-card p-5">
              <h3 className="text-base font-semibold">Win rate by deal size</h3>
              <p className="text-xs text-zinc-500 mb-4">Where you convert best</p>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={data.win_by_size}>
                  <CartesianGrid stroke="#27272A" vertical={false} />
                  <XAxis dataKey="bucket" {...chartAxis} />
                  <YAxis {...chartAxis} unit="%" />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Line type="monotone" dataKey="win_rate" stroke="#10B981" strokeWidth={2} dot={{ r: 4, fill: "#10B981" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-4">
            {[
              { key: "overdue", title: "Needs a nudge", hint: "Sent 7+ days ago, no decision", icon: Clock, rows: data.worklists.overdue, meta: (r) => `${r.days_since_sent}d ago` },
              { key: "expiring", title: "Expiring soon", hint: "Link expires within 5 days", icon: AlertTriangle, rows: data.worklists.expiring, meta: (r) => `${r.days_left}d left` },
              { key: "viewed_not_accepted", title: "Viewed, not accepted", hint: "Warm and worth a call", icon: Eye, rows: data.worklists.viewed_not_accepted, meta: (r) => `${r.opens} opens` },
            ].map(({ key, title, hint, icon: Icon, rows, meta }) => (
              <div key={key} data-testid={`worklist-${key}`} className="nv-card p-5">
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4 text-indigo-400" />
                  <h3 className="text-base font-semibold">{title}</h3>
                </div>
                <p className="text-xs text-zinc-500 mb-3">{hint}</p>
                {!rows.length ? (
                  <p className="text-xs text-zinc-600 py-4">Nothing here right now.</p>
                ) : (
                  <div className="divide-y divide-zinc-800/60">
                    {rows.map((r) => (
                      <button
                        key={r.id}
                        data-testid={`worklist-row-${r.id}`}
                        onClick={() => navigate(`/editor/${r.id}`)}
                        className="w-full text-left py-2.5 flex items-center justify-between gap-3 hover:bg-zinc-800/30 -mx-2 px-2 rounded transition-colors"
                      >
                        <div className="min-w-0">
                          <p className="text-sm text-zinc-200 truncate">{r.title}</p>
                          <p className="text-xs text-zinc-500 truncate">{r.client_name}</p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-xs font-num text-zinc-300">{r.total_formatted}</p>
                          <p className="text-[11px] text-zinc-500 font-num">{meta(r)}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {data.win_by_service.length > 0 && (
            <div data-testid="win-by-service" className="nv-card p-5">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-indigo-400" />
                <h3 className="text-base font-semibold">Win rate by industry</h3>
              </div>
              <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {data.win_by_service.map((s) => (
                  <div key={s.service} className="rounded-lg border border-zinc-800/70 p-3">
                    <div className="flex justify-between items-baseline">
                      <p className="text-sm text-zinc-300 truncate">{s.service}</p>
                      <p className="text-sm font-num text-emerald-400">{s.win_rate}%</p>
                    </div>
                    <div className="mt-2 h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                      <div className="h-full bg-emerald-500 transition-all duration-700" style={{ width: `${s.win_rate}%` }} />
                    </div>
                    <p className="mt-1.5 text-[11px] text-zinc-500 font-num">
                      {s.won}/{s.total} won
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </AppShell>
  );
}
